import {
  ExcludeDescendants,
  NodesResolveRequest,
  StartNodeCondition,
} from '@agilelab/plugin-wb-marketplace-common';
import { TemplateColumn } from '@agilelab/plugin-wb-platform';

// ── SKOS options type ─────────────────────────────────────────────────────────

/**
 * Options accepted by the BusinessConcept provider when `type: BusinessConcept`
 * is set in a template's entity list. Fields are placed at the same level as
 * other `TemplateEntity` properties (type, displayName, returnField, …).
 */
export interface SkosOptions {
  /** Vocabulary preset. Currently only `'skos'` is supported. */
  preset: 'skos';
  /**
   * Filter results to concepts that belong to one or more concept schemes.
   * Each entry is a full IRI or a plain-text skos:prefLabel.
   * Mutually exclusive with `conceptFilter`.
   */
  conceptSchemeFilter?: string[];
  /**
   * Filter results to a specific set of seed concepts.
   * Each entry is a full IRI or a plain-text skos:prefLabel.
   * Mutually exclusive with `conceptSchemeFilter`.
   */
  conceptFilter?: string[];
  /**
   * When true, the initial tree view shows the first-level children of the
   * start nodes instead of the start nodes themselves. Defaults to false.
   */
  expandStartNodes?: boolean;
  /**
   * RDF predicate (IRI or prefixed name) used as the display label for each
   * result. Mapped from the `displayField` TemplateEntity property.
   * Defaults to `skos:prefLabel`.
   */
  labelProperty?: string;
  /** Column definitions for the picker table. Defaults to a single skos:prefLabel column. */
  columns?: TemplateColumn[];
  /**
   * Forward RDF relations to follow when expanding a node.
   * Overrides the default `['skos:narrower', 'skos:hasTopConcept']`.
   * Example: add `'skos:member'` for SKOS-XL concept collections.
   */
  relations?: string[];
  /**
   * Inverse RDF relations to follow when expanding a node.
   * Overrides the default `['skos:broader']`.
   * Vocabularies without `skos:hasTopConcept` declarations that rely solely
   * on `skos:inScheme` to link a scheme to its concepts can add
   * `'skos:inScheme'` here, at the cost of concepts also appearing as direct
   * children of the scheme in addition to their real parent.
   */
  inverseRelations?: string[];
  /**
   * Forward RDF relations to follow specifically when expanding a
   * `skos:ConceptScheme` node (as opposed to a `skos:Concept` node).
   * Defaults to `undefined` (none), since the default scheme → concepts
   * entry point is `skos:inScheme` (an inverse relation, see
   * `schemeInverseRelations`). Set this (e.g. to `['skos:hasTopConcept']`)
   * for vocabularies that declare explicit top concepts.
   */
  schemeRelations?: string[];
  /**
   * Inverse RDF relations to follow specifically when expanding a
   * `skos:ConceptScheme` node. Defaults to `['skos:inScheme']`, combined
   * with `excludeDescendants` pruning (default: via `skos:broader`) to
   * avoid the duplicate-rows problem of every scheme member appearing as a
   * direct child. Vocabularies that declare `skos:hasTopConcept` can
   * override this to `[]` and set `schemeRelations: ['skos:hasTopConcept']`
   * instead.
   */
  schemeInverseRelations?: string[];
  /**
   * When expanding a `skos:ConceptScheme` node, excludes children that can
   * reach — within `maxDepth` hops — an ancestor that is ALSO a member of
   * the scheme. Used to compute the true top-level concepts of a scheme
   * when membership is only declared via `skos:inScheme` rather than an
   * explicit `skos:hasTopConcept`. Defaults to
   * `{ childToAncestorRelations: ['skos:broader'], maxDepth: 6 }`. See
   * {@link ExcludeDescendants}.
   */
  excludeDescendants?: ExcludeDescendants;
  /** @internal — not used in the provider context, kept for translateSkosToRdf compatibility. */
  allowMultipleSelections?: boolean;
  /** @internal — not used in the provider context, kept for translateSkosToRdf compatibility. */
  outputFormat?: 'default' | 'object';
  /**
   * Number of children fetched per page when expanding or paginating a tree
   * node. Defaults to 20.
   */
  childrenPageSize?: number;
}

// ── SKOS constants ────────────────────────────────────────────────────────────

const SKOS_CONCEPT_TYPE = 'skos:Concept';
const SKOS_CONCEPT_SCHEME_TYPE = 'skos:ConceptScheme';

/**
 * Default forward relations for SKOS traversal between concepts.
 * - skos:narrower: concept → sub-concept
 *
 * Scheme → concept traversal (root/start-node expansion) is handled
 * separately by `schemeExpansion`'s default of `skos:inScheme` combined with
 * `excludeDescendants` pruning — see `translateSkosToRdf` below — so
 * `skos:hasTopConcept` is no longer needed as a default here. Vocabularies
 * that declare `skos:hasTopConcept` can still opt into it explicitly via
 * `schemeRelations: ['skos:hasTopConcept']`.
 */
const SKOS_FORWARD_RELATIONS = ['skos:narrower'];

/**
 * Default inverse relations for SKOS traversal between concepts.
 * - skos:broader: concept → parent concept
 */
const SKOS_INVERSE_RELATIONS = ['skos:broader'];

/** Property used to fetch display labels from the API. */
const SKOS_PREF_LABEL = 'skos:prefLabel';

/**
 * Default for `excludeDescendants.maxDepth`, mirrored from the backend's
 * `EXCLUDE_DESCENDANTS.DEFAULT_MAX_DEPTH` constant (kept in sync manually
 * since frontend and backend live in separate packages). There is no upper
 * bound: `maxDepth` is fine-tuned per vocabulary depth by whoever configures
 * the template, only a sane minimum of 1 is enforced.
 */
const EXCLUDE_DESCENDANTS_DEFAULT_MAX_DEPTH = 10;

// ── IRI detection ─────────────────────────────────────────────────────────────

/**
 * Returns true when the string looks like an IRI (absolute or prefixed).
 *
 * - Absolute IRI: contains "://"
 * - Prefixed name: matches `prefix:localName` (e.g. "ex:BankingVocabulary")
 */
function isIri(value: string): boolean {
  return value.includes('://') || /^[a-zA-Z][a-zA-Z0-9_-]*:[^/\s]/.test(value);
}

/**
 * Returns true when a `conceptSchemeFilter` / `conceptFilter` entry is a
 * Nunjucks expression (`{{ }}`) rather than a static IRI or plain-text
 * skos:prefLabel value.
 */
export function containsNunjucksExpression(value: string): boolean {
  return value.includes('{{');
}

// ── Condition builders ────────────────────────────────────────────────────────

function buildConceptSchemeConditions(entry: string): StartNodeCondition[] {
  const typeCondition: StartNodeCondition = {
    predicate: 'rdf:type',
    equalsIRI: SKOS_CONCEPT_SCHEME_TYPE,
  };
  if (isIri(entry)) {
    return [{ predicate: '@id', in: [entry] }, typeCondition];
  }
  return [{ predicate: SKOS_PREF_LABEL, equalsLiteral: entry }, typeCondition];
}

function buildConceptConditions(entry: string): StartNodeCondition[] {
  const typeCondition: StartNodeCondition = {
    predicate: 'rdf:type',
    equalsIRI: SKOS_CONCEPT_TYPE,
  };
  if (isIri(entry)) {
    return [{ predicate: '@id', in: [entry] }, typeCondition];
  }
  return [{ predicate: SKOS_PREF_LABEL, equalsLiteral: entry }, typeCondition];
}

// ── Output type ───────────────────────────────────────────────────────────────

export type TranslatedRdfProps = {
  startNode: NodesResolveRequest['startNode'];
  traverse: NodesResolveRequest['traverse'];
  returnOptions: NodesResolveRequest['return'];
  prefixMap: NodesResolveRequest['prefixMap'];
  labelProperty: string;
  columns: TemplateColumn[];
  allowMultipleSelections: boolean | undefined;
  outputFormat: 'default' | 'object' | undefined;
  expandStartNodes: boolean;
  childrenPageSize: number | undefined;
  /**
   * Traversal + filtering to use specifically when expanding a
   * `skos:ConceptScheme` node, instead of `traverse`. Only populated when
   * `schemeRelations`, `schemeInverseRelations`, or `excludeDescendants` is
   * set in the input `SkosOptions`.
   */
  schemeExpansion?: {
    /** `rdf:type` value that triggers this expansion mode (`skos:ConceptScheme`). */
    nodeType: string;
    traverse?: {
      relations?: string[];
      inverseRelations?: string[];
    };
    excludeDescendants?: ExcludeDescendants;
  };
};

// ── Main translation function ─────────────────────────────────────────────────

/**
 * Translates SKOS options (from a TemplateEntity's `BusinessConcept` configuration)
 * into raw `NodesResolveRequest` parameters forwarded to the KGM `/nodes/resolve` API.
 *
 * Rules for `preset: "skos"`:
 * - Forward relations (concept ↔ concept):  skos:narrower
 * - Inverse relations (concept ↔ concept):  skos:broader
 * - Scheme expansion (scheme → concepts): defaults to skos:inScheme, pruned
 *   via excludeDescendants (skos:broader) to only true top-level concepts.
 *   Override with schemeRelations/schemeInverseRelations for vocabularies
 *   that declare skos:hasTopConcept.
 * - conceptSchemeFilter → startNode DNF with rdf:type = skos:ConceptScheme
 * - conceptFilter       → startNode DNF with rdf:type = skos:Concept
 * - labelProperty defaults to skos:prefLabel
 * - return.properties always includes skos:prefLabel; additional predicates are
 *   inferred from the column path expressions when present.
 * - expandStartNodes defaults to false; when true, the initial tree view shows
 *   the first-level children of the start nodes instead of the start nodes.
 */
export function translateSkosToRdf(options: SkosOptions): TranslatedRdfProps {
  const {
    conceptSchemeFilter,
    conceptFilter,
    allowMultipleSelections,
    outputFormat,
    columns,
    expandStartNodes,
    childrenPageSize,
    schemeRelations,
    schemeInverseRelations,
    excludeDescendants,
  } = options;

  // ── startNode ────────────────────────────────────────────────────────────
  let startNode: NodesResolveRequest['startNode'];

  if (conceptSchemeFilter && conceptSchemeFilter.length > 0) {
    startNode = conceptSchemeFilter.map(buildConceptSchemeConditions);
  } else if (conceptFilter && conceptFilter.length > 0) {
    startNode = conceptFilter.map(buildConceptConditions);
  } else {
    // Default: start from all concept schemes so the picker returns all
    // concepts without requiring an explicit filter.
    startNode = [
      [{ predicate: 'rdf:type', equalsIRI: SKOS_CONCEPT_SCHEME_TYPE }],
    ];
  }

  // ── traverse (SKOS defaults, overridable per template) ─────────────────────
  const traverse: NodesResolveRequest['traverse'] = {
    relations: options.relations ?? SKOS_FORWARD_RELATIONS,
    inverseRelations: options.inverseRelations ?? SKOS_INVERSE_RELATIONS,
  };

  // ── return.properties ────────────────────────────────────────────────────
  const propertySet = new Set<string>([SKOS_PREF_LABEL]);
  if (columns) {
    for (const col of columns) {
      const path = 'path' in col ? col.path : undefined;
      if (!path) continue;
      if (path.includes('{{')) {
        const matches = path.matchAll(/properties\[['"]([^'"]+)['"]\]/g);
        for (const m of matches) {
          propertySet.add(m[1]);
        }
      } else if (path !== 'id' && path !== 'type') {
        propertySet.add(path);
      }
    }
  }

  const returnOptions: NodesResolveRequest['return'] = {
    properties: Array.from(propertySet),
    // Always true so ConceptSchemes/root concepts appear as the tree roots and
    // in search results. Tree mode further overrides this per-request based on
    // `expandStartNodes` (see RdfConceptsProvider).
    includeStartNodes: true,
  };

  // ── labelProperty ────────────────────────────────────────────────────────
  const labelProperty = options.labelProperty ?? SKOS_PREF_LABEL;

  // ── columns ──────────────────────────────────────────────────────────────
  const resolvedColumns = columns ?? [{ name: 'Label', path: SKOS_PREF_LABEL }];

  // ── schemeExpansion (scheme-level traversal, defaults to inScheme + pruning) ──
  // Defaults to `skos:inScheme` as the scheme → concepts entry point (works
  // for vocabularies without `skos:hasTopConcept`), combined with
  // `excludeDescendants` pruning via `skos:broader` so only true top-level
  // concepts (those with no broader parent within maxDepth hops) are shown
  // as direct children of the scheme — avoiding the duplicate-rows problem
  // of every scheme member appearing as a direct child. Vocabularies that
  // declare `skos:hasTopConcept` can override via `schemeRelations` /
  // `schemeInverseRelations`.
  const {
    childToAncestorRelations: resolvedChildToAncestor = ['skos:broader'],
    ancestorToChildRelations: resolvedAncestorToChild = [],
    maxDepth: resolvedMaxDepth,
  } = excludeDescendants ?? {};

  if (
    resolvedChildToAncestor.length === 0 &&
    resolvedAncestorToChild.length === 0
  ) {
    throw new Error(
      'excludeDescendants requires at least one of childToAncestorRelations ' +
        'or ancestorToChildRelations to be non-empty',
    );
  }

  const schemeExpansion: TranslatedRdfProps['schemeExpansion'] = {
    nodeType: SKOS_CONCEPT_SCHEME_TYPE,
    traverse: {
      relations: schemeRelations,
      inverseRelations: schemeInverseRelations ?? ['skos:inScheme'],
    },
    excludeDescendants: {
      childToAncestorRelations: resolvedChildToAncestor,
      ancestorToChildRelations: resolvedAncestorToChild,
      maxDepth: Math.max(
        1,
        resolvedMaxDepth ?? EXCLUDE_DESCENDANTS_DEFAULT_MAX_DEPTH,
      ),
    },
  };

  return {
    startNode,
    traverse,
    returnOptions,
    prefixMap: { skos: 'http://www.w3.org/2004/02/skos/core#' },
    labelProperty,
    columns: resolvedColumns,
    allowMultipleSelections,
    outputFormat,
    expandStartNodes: expandStartNodes ?? false,
    childrenPageSize,
    schemeExpansion,
  };
}
